/**
 * Onboarding step 5 of 7 — Meet your genie.
 *
 * The genie is REAL by this point: step 4 created it. So this step shows what
 * was actually built (its saved name, its saved greeting, and what it really
 * learned from the website), offers REAL voices with real previews, and saves
 * the owner's voice and personality choices onto the genie itself.
 *
 * It used to show four invented voice names, a hardcoded "142 pages", and
 * wrote choices only into client state where they died.
 *
 * Brand law: genie-* tokens + signature gradient only; *Voice* never named here;
 * no "AI" language; no em dashes; Lucide for inline icons only.
 */
import { useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { BookOpen, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { WelcomeFrame } from "@/components/voice-onboarding/WelcomeFrame";
import { VoiceMascot } from "@/components/voice-genie/VoiceMascot";
import { useOnboarding } from "@/contexts/voice-onboarding/OnboardingContext";
import { useAgent, useUpdateAgent } from "@/hooks/agent/useAgents";
import { usePreselectedAndFavoriteVoices } from "@/hooks/voice/usePreselectedVoices";
import {
  TRAIT_OPTIONS,
  buildTraitLine,
  readTraits,
  stripTraitLine,
  type TraitLabel,
} from "@/lib/agent/personalityTraits";
import { ONBOARD_ROUTES, VOICE_ONBOARD_STEPS } from "./steps";
import { TraitChip, VoiceOption } from "./MeetBits";

/** How many voices to surface without turning this into the voice playground. */
const VOICE_LIMIT = 6;

const DEFAULT_TRAITS: TraitLabel[] = ["Professional", "Friendly"];

const MeetPage = () => {
  const navigate = useNavigate();
  const { genieType, business, draft, setDraft } = useOnboarding();

  const { data: agent } = useAgent(draft.genieId ?? "");
  const updateAgent = useUpdateAgent();
  const { voices, isLoading: voicesLoading } = usePreselectedAndFavoriteVoices();

  const shortlist = useMemo(() => voices.slice(0, VOICE_LIMIT), [voices]);

  const conversationConfig = agent?.settings?.conversation_config;
  const promptText = conversationConfig?.agent?.prompt?.prompt ?? "";

  const [voiceId, setVoiceId] = useState<string>(
    () => draft.voiceId ?? conversationConfig?.tts?.voice_id ?? "",
  );
  const [traits, setTraits] = useState<TraitLabel[]>(() => {
    const fromPrompt = promptText ? readTraits(promptText) : [];
    return fromPrompt.length ? fromPrompt : DEFAULT_TRAITS;
  });
  const [saving, setSaving] = useState(false);

  // Real preview playback, the same way the immersive setup panel does it.
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [playingVoiceId, setPlayingVoiceId] = useState<string | null>(null);

  const genieName = agent?.name ?? draft.genieName ?? genieType?.title ?? "Your genie";
  const mascot = genieType?.mascot ?? "receptionist";
  const greeting =
    conversationConfig?.agent?.first_message ||
    business.greeting?.trim() ||
    `Kia ora, you've reached ${business.name?.trim() || "us"}. How can I help?`;
  const site = business.website?.replace(/^https?:\/\//, "").replace(/\/$/, "");

  /** What the genie actually knows, from the real build. Never invented. */
  const knowledgeLine = (() => {
    const summary = draft.knowledgeSummary;
    if (summary?.ok) {
      return summary.pages
        ? `Knows ${site ?? "your site"} · ${summary.pages} pages read`
        : `Knows ${site ?? "your site"}`;
    }
    if (summary?.attempted) {
      return `We could not read ${site ?? "your site"} yet. You can add knowledge later.`;
    }
    return "No knowledge added yet. You can add it any time.";
  })();

  const playPreview = (previewUrl?: string, id?: string) => {
    if (!previewUrl) {
      toast("No preview available for this voice yet");
      return;
    }
    audioRef.current?.pause();
    const audio = new Audio(previewUrl);
    audioRef.current = audio;
    setPlayingVoiceId(id ?? null);
    audio.onended = () => setPlayingVoiceId(null);
    void audio.play().catch(() => setPlayingVoiceId(null));
  };

  const selectVoice = (id: string, previewUrl?: string, label?: string) => {
    setVoiceId(id);
    setDraft((d) => ({ ...d, voiceId: id, voiceLabel: label ?? d.voiceLabel }));
    playPreview(previewUrl, id);
  };

  const toggleTrait = (label: TraitLabel) =>
    setTraits((prev) => (prev.includes(label) ? prev.filter((t) => t !== label) : [...prev, label]));

  /**
   * Save the two choices that matter onto the REAL genie, using the same
   * deep-merge shape the immersive setup panel uses. If there is no genie
   * (creation failed upstream) the choices stay in the draft and we move on,
   * rather than blocking the flow on something the owner cannot fix here.
   */
  const handleContinue = async () => {
    setDraft((d) => ({ ...d, voiceId, personalityTraits: traits.slice() }));

    if (!agent) {
      navigate(ONBOARD_ROUTES[5]);
      return;
    }

    const basePrompt = stripTraitLine(promptText);
    const traitLine = buildTraitLine(traits);
    const newPrompt = traitLine ? `${basePrompt}\n${traitLine}`.trim() : basePrompt;

    setSaving(true);
    try {
      await updateAgent.mutateAsync({
        agent,
        updateData: {
          settings: {
            ...agent.settings,
            conversation_config: {
              ...conversationConfig,
              agent: {
                ...conversationConfig?.agent,
                prompt: { ...conversationConfig?.agent?.prompt, prompt: newPrompt },
              },
              ...(voiceId ? { tts: { ...conversationConfig?.tts, voice_id: voiceId } } : {}),
            },
          },
        },
      });
    } catch {
      toast.error("We could not save those changes. You can set them on your genie's page later.");
    } finally {
      setSaving(false);
      navigate(ONBOARD_ROUTES[5]);
    }
  };

  return (
    <WelcomeFrame
      step={5}
      steps={VOICE_ONBOARD_STEPS}
      eyebrow="MEET YOUR GENIE"
      title={`${genieName} is ready`}
      subtitle="Here's what we built from your details. Keep it as is, or make the two changes that matter most."
      showMascot={false}
      continueLabel={saving ? "Saving..." : "Sounds great"}
      continueDisabled={saving}
      onBack={() => navigate(ONBOARD_ROUTES[3])}
      onContinue={() => void handleContinue()}
      onSkip={() => navigate("/genies")}
      onSkipStep={() => navigate(ONBOARD_ROUTES[5])}
    >
      <div className="w-full flex flex-col gap-6">
        {/* genie-summary */}
        <div
          data-hg-block="genie-summary"
          className="flex items-center gap-5 p-5 rounded-2xl border border-genie-border bg-white shadow-sm"
        >
          <div className="w-16 h-16 rounded-full bg-genie-primary/10 flex items-center justify-center overflow-hidden flex-shrink-0">
            <VoiceMascot mascot={mascot} size={54} />
          </div>
          <div className="flex-1 min-w-0 flex flex-col gap-1">
            <p className="text-[17px] font-bold text-[#222B45] tracking-[-0.2px]">{genieName}</p>
            <p className="text-[13px] text-[#64748B] leading-snug">"{greeting}"</p>
            <div className="flex items-center gap-1.5 mt-0.5">
              <BookOpen className="w-3.5 h-3.5 text-genie-primary" strokeWidth={1.8} />
              <p className="text-xs text-gray-500">{knowledgeLine}</p>
            </div>
          </div>
        </div>

        {/* voice-pick — real voices, real previews */}
        <div data-hg-block="voice-pick" className="flex flex-col gap-2">
          <p className="text-[12.5px] font-bold text-[#64748B]">Voice</p>
          {voicesLoading ? (
            <span className="inline-flex items-center gap-2 text-[13px] text-gray-500">
              <Loader2 className="w-3.5 h-3.5 animate-spin text-genie-primary" />
              Loading voices
            </span>
          ) : shortlist.length === 0 ? (
            <p className="text-[13px] text-gray-500">
              Your genie is using its default voice. You can change it on its page any time.
            </p>
          ) : (
            <div className="flex flex-wrap gap-2">
              {shortlist.map((v) => (
                <VoiceOption
                  key={v.id}
                  preset={{
                    name: v.name,
                    tag: v.labels?.accent
                      ? `${v.labels.accent}${v.labels.gender ? ` / ${v.labels.gender}` : ""}`
                      : (v.description ?? "Tap to hear"),
                  }}
                  selected={voiceId === v.id}
                  playing={playingVoiceId === v.id}
                  onSelect={() => selectVoice(v.id, v.preview_url, v.name)}
                />
              ))}
            </div>
          )}
        </div>

        {/* personality-pick */}
        <div data-hg-block="personality-pick" className="flex flex-col gap-2">
          <p className="text-[12.5px] font-bold text-[#64748B]">Personality</p>
          <div className="flex flex-wrap gap-2">
            {TRAIT_OPTIONS.map((label) => (
              <TraitChip
                key={label}
                label={label}
                on={traits.includes(label)}
                onToggle={() => toggleTrait(label)}
              />
            ))}
          </div>
        </div>
      </div>
    </WelcomeFrame>
  );
};

export default MeetPage;
